import { Input, InputNumber, Row, Col, Select } from 'antd';
import { useIntl } from 'react-intl';
import { sortBy } from 'lodash';

import { Form, WrapperSelect } from 'common/components';
import { OverlayInputWrapper } from 'common/components/overlay';
import { ResetFormStatus } from 'common/components/product-add/make-product-active/product-form/ResetFormStatus';
import { ChargeActiveProduct } from 'common/components/product-add/make-product-active/product-form/ChargeActiveProduct';

import messages from 'i18n/messages/product';

import { useGetEnumsByEnumName, useGetMemberInfo, useUserInfo } from 'hooks';
import { useCheckPermissions } from 'hooks/useCheckPermissions';

import { useGetMemberHeaderFromMemberId } from 'common/queries/member-module';

import {
  disableStatusSelectionInEditMode,
  disableStatusSelectionInEditModeInCreateMode,
  enableMakeActiveProduct,
} from 'utils/ixone-capture';

import { STATUS_PRODUCT } from 'static/Constants';
import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';
import {
  DEFAULT_TARGET_MARKET,
  DEFAULT_UNIT_COUNT,
  LENGTH_PRODUCT_DESCRIPTION,
  LENGTH_PRODUCT_NAME,
  numericInputStyles,
} from 'common/components/product-add/components/constants';

import {
  checkActiveStatus,
  checkRequiredPrimaryFieldForm,
  clearValidatedFieldErrors,
  getProductDescriptionValidationRules,
  getRestrictedTextValidationRules,
  getStandardTextValidationRules,
  isUnitPackageLevel,
} from 'common/components/product-add/components/utils/validation';

import { sortTargetMarket } from 'common/components/product-add/components/utils/sortTargetMarket';
import { autoPopulateBarcodeFields } from 'common/components/product-add/components/utils/barcode';

const { Option } = Select;

const fullWidthFormLayout = {
  labelCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 8,
    },
  },
  wrapperCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 16,
    },
  },
  labelAlign: 'left',
};

const splitRowLeftLayout = {
  labelCol: { span: 16 },
  wrapperCol: { span: 8 },
  labelAlign: 'left',
};

const splitRowRightLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
  labelAlign: 'right',
};

export const ProductFieldset = (props) => {
  const intl = useIntl();

  const { getOverlayProps, productEnums, targetMarketList, isFieldEditable } =
    props;

  const productBasicUOM = productEnums?.filter(
    (type) => type.enumName === 'BasicUomEnum'
  );

  const countryCodeEnums = useGetEnumsByEnumName('CountryCodeEnum');

  const enumProperties =
    productBasicUOM?.[0]?.enumProperties?.filter((type) =>
      ['Mass', 'Count', 'Volume', 'Dimensions', 'Area'].includes(type.enumGroup)
    ) || [];

  // Sort target markets with US first

  const sortCountryOfOrigin = () => {
    if (!countryCodeEnums) return [];

    const usCountry = countryCodeEnums.find((country) => {
      return country?.enumCode === '840';
    });

    const nonUsCountries = countryCodeEnums.filter((country) => {
      return country?.enumCode !== '840';
    });

    const sortedNonUsCountries = sortBy(
      [...nonUsCountries],
      ['enumDisplayName']
    );

    return usCountry
      ? [usCountry, ...sortedNonUsCountries]
      : sortedNonUsCountries;
  };

  const sortedCountryOfOrigin = sortCountryOfOrigin();

  const sortedTargetMarket = sortTargetMarket(targetMarketList);

  const packageLevel = Form.useWatch('packageLevel');
  const status = Form.useWatch('status');

  const isUnit = isUnitPackageLevel(packageLevel);
  const isActiveStatus = checkActiveStatus(status);

  const isRequired = checkRequiredPrimaryFieldForm({
    isActiveStatus,
    isUnit,
  });

  return (
    <>
      <Form.Item
        label='Product Name'
        name='productName'
        rules={
          isRequired
            ? [
                {
                  required: true,
                  message: intl.formatMessage(messages.productNameRequired),
                },
                {
                  max: LENGTH_PRODUCT_NAME,
                  message: `Product Name must not exceed ${LENGTH_PRODUCT_NAME} characters.`,
                },
                ...getRestrictedTextValidationRules('Product Name'),
              ]
            : [
                {
                  required: true,
                  message: intl.formatMessage(messages.productNameRequired),
                },
              ]
        }
        {...fullWidthFormLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('productName')}>
          <Input
            maxLength={isUnit ? LENGTH_PRODUCT_NAME : undefined}
            showCount={isUnit}
            style={{ flex: 1 }}
            disabled={isFieldEditable('productName')}
          />
        </OverlayInputWrapper>
      </Form.Item>

      <Form.Item
        label='Product Description'
        name='productDescription'
        rules={
          isRequired
            ? [
                {
                  required: true,
                  message: intl.formatMessage(
                    messages.productDescriptionRequired
                  ),
                },
                ...getProductDescriptionValidationRules(),
              ]
            : []
        }
        {...fullWidthFormLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('productDescription')}>
          <Input.TextArea
            rows={6}
            maxLength={isUnit ? LENGTH_PRODUCT_DESCRIPTION : undefined}
            showCount={isUnit}
            disabled={isFieldEditable('productDescription')}
          />
        </OverlayInputWrapper>
      </Form.Item>

      <Row gutter={4} style={{ marginTop: 24 }}>
        <Col span={12}>
          <Form.Item
            label='Net Contents Value'
            name='netContentsValue'
            rules={[
              {
                required: isRequired,
                message: 'Net Contents Value is required',
              },
            ]}
            {...splitRowLeftLayout}
          >
            <OverlayInputWrapper {...getOverlayProps('netContentsValue')}>
              <InputNumber
                style={numericInputStyles}
                min={0}
                max={999999}
                precision={3}
                disabled={isFieldEditable('netContentsValue')}
              />
            </OverlayInputWrapper>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label='UOM'
            name='netContentsUom'
            rules={[
              {
                required: isRequired,
                message: 'UOM is required',
              },
            ]}
            {...splitRowRightLayout}
          >
            <OverlayInputWrapper {...getOverlayProps('netContentsUom')}>
              <WrapperSelect
                showSearch
                filterOption={(input, option) =>
                  option.props.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                getPopupContainer={() =>
                  document.getElementById('product-field')
                }
                disabled={isFieldEditable('netContentsUom')}
              >
                {sortBy([...enumProperties], ['enumDisplayName']).map(
                  (type, index) => {
                    return (
                      <Select.Option value={type?.enumDisplayName} key={index}>
                        {type?.enumDisplayName}
                      </Select.Option>
                    );
                  }
                )}
              </WrapperSelect>
            </OverlayInputWrapper>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label='Net Contents Statement'
        name='netContentsStatement'
        rules={[
          {
            required: isRequired,
            message: 'Net Contents Statement is required',
          },
          ...getStandardTextValidationRules(),
        ]}
        {...fullWidthFormLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('netContentsStatement')}>
          <Input disabled={isFieldEditable('netContentsStatement')} />
        </OverlayInputWrapper>
      </Form.Item>

      <Row gutter={4}>
        <Col span={12}>
          <Form.Item
            label='Primary Size'
            name='primarySize'
            rules={[
              {
                required: isRequired,
                message: 'Primary Size is required',
              },
            ]}
            {...splitRowLeftLayout}
          >
            <OverlayInputWrapper {...getOverlayProps('primarySize')}>
              <InputNumber
                style={numericInputStyles}
                min={0}
                max={999999}
                precision={3}
                disabled={isFieldEditable('primarySize')}
              />
            </OverlayInputWrapper>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label='UOM'
            name='primarySizeUOM'
            rules={[
              {
                required: isRequired,
                message: 'UOM is required',
              },
            ]}
            {...splitRowRightLayout}
          >
            <OverlayInputWrapper {...getOverlayProps('primarySizeUOM')}>
              <WrapperSelect
                showSearch
                filterOption={(input, option) =>
                  option.props.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                getPopupContainer={() =>
                  document.getElementById('product-field')
                }
                disabled={isFieldEditable('primarySizeUOM')}
              >
                {sortBy([...enumProperties], ['enumDisplayName']).map(
                  (type, index) => {
                    return (
                      <Select.Option value={type?.enumDisplayName} key={index}>
                        {type?.enumDisplayName}
                      </Select.Option>
                    );
                  }
                )}
              </WrapperSelect>
            </OverlayInputWrapper>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label='Unit Count'
        name='unitCount'
        rules={[
          {
            required: isRequired,
            message: 'Unit Count is required',
          },
        ]}
        {...fullWidthFormLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('unitCount')}>
          <InputNumber
            type='number'
            min={0}
            precision={0}
            style={numericInputStyles}
            disabled={isFieldEditable('unitCount')}
          />
        </OverlayInputWrapper>
      </Form.Item>

      <Form.Item
        label='Country of Origin'
        name='countryOfOrigin'
        {...fullWidthFormLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('countryOfOrigin')}>
          <WrapperSelect
            mode='multiple'
            allowClear
            showSearch
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            getPopupContainer={() => document.getElementById('product-field')}
            disabled={isFieldEditable('countryOfOrigin')}
          >
            {sortedCountryOfOrigin?.map((item, index) => (
              <Select.Option key={index} value={item?.enumDisplayName}>
                {item?.enumDisplayName}
              </Select.Option>
            ))}
          </WrapperSelect>
        </OverlayInputWrapper>
      </Form.Item>

      <Form.Item
        label='Target Market'
        name='targetMarket'
        rules={[
          {
            required: isRequired,
            message: 'Target Market is required',
          },
        ]}
        {...fullWidthFormLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('targetMarket')}>
          <WrapperSelect
            allowClear
            showSearch
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            getPopupContainer={() => document.getElementById('product-field')}
            disabled={isFieldEditable('targetMarket')}
          >
            {sortedTargetMarket?.map((item, index) => (
              <Select.Option key={index} value={item?.Country}>
                {item?.Country}
              </Select.Option>
            ))}
          </WrapperSelect>
        </OverlayInputWrapper>
      </Form.Item>
    </>
  );
};

export const SecurityFieldset = (props) => {
  const form = Form.useFormInstance();

  const {
    isEdit,
    getOverlayProps,
    productData,
    memberIdBrand,
    formMode,
    onRevalidateProduct,
    setValidateErrors,
    hideActiveButton,
    statusValidateProps,
    validateProducts,
    productImagery,
    getFormPayload,
  } = props;

  const { data: memberHeaderOfProductCreator } = useGetMemberHeaderFromMemberId(
    {
      memberId: memberIdBrand,
      enabled: !!memberIdBrand,
    }
  );

  const isPrivateVisibilityProduct = () => {
    if (formMode === 'create') {
      if (companyInfo || memberIdBrand) {
        return (
          memberHeaderOfProductCreator?.products?.toLowerCase() === 'private'
        );
      }
    }

    if (formMode === 'edit') {
      return memberHeaderOfProductOwner?.products?.toLowerCase() === 'private';
    }

    //* default return;
    return memberHeaderOfProductCreator?.products?.toLowerCase() === 'private';
  };

  const isActiveStatus = productData?.status?.toLowerCase() === 'active';

  const { memberInfo: companyInfo } = useGetMemberInfo(memberIdBrand);

  const { data: memberHeaderOfProductOwner } = useGetMemberHeaderFromMemberId({
    memberId: productData?.ownerId,
    enabled: Boolean(productData && productData?.ownerId),
  });

  /* match conditions for making active product and status is not active  */
  const isDisplayActiveProductBtn =
    enableMakeActiveProduct(productData) &&
    !isActiveStatus &&
    isEdit &&
    !hideActiveButton;

  const handleStatusChange = async (value) => {
    const isActiveStatus = value?.toLowerCase() === 'active';

    if (isActiveStatus) {
      const unitCount = form.getFieldValue('unitCount');
      const targetMarket = form.getFieldValue('targetMarket');

      form.setFieldsValue({
        ...form.getFieldsValue(),
        unitCount: unitCount || DEFAULT_UNIT_COUNT,
        targetMarket: targetMarket || DEFAULT_TARGET_MARKET,
      });

      await autoPopulateBarcodeFields(form);
      await onRevalidateProduct();
    } else {
      clearValidatedFieldErrors(form);
      setValidateErrors();
    }
  };

  const isPrivateLabel = Form.useWatch('isPrivateLevel');
  const { userInfo } = useUserInfo();

  const isRestrictedManufacturer =
    userInfo?.member?.memberType === 'Manufacturer' && !userInfo?.member?.tcp;

  const disableVisibility = productData?.gdsnShield || isRestrictedManufacturer;
  const hasBypassProductInternalPermission = useCheckPermissions([
    {
      action: ABILITY_ACTION.BYPASS,
      subject: ABILITY_SUBJECT.PRODUCT_INTERNAL_MAINTENANCE,
    },
  ]);

  return (
    <>
      <Form.Item name='visibility' label='Visibility' {...fullWidthFormLayout}>
        <OverlayInputWrapper {...getOverlayProps('visibility')}>
          {isPrivateVisibilityProduct() ? (
            <WrapperSelect
              disabled={disableVisibility}
              getPopupContainer={() => document.getElementById('product-field')}
            >
              <Option value='Only If Shared'>Only If Shared</Option>
              <Option value='Private'>Private</Option>
            </WrapperSelect>
          ) : (
            <WrapperSelect
              disabled={disableVisibility}
              getPopupContainer={() => document.getElementById('product-field')}
            >
              <Option value='Only If Shared'>Only If Shared</Option>
              <Option value='Public'>Public</Option>
              <Option value='Private'>Private</Option>
            </WrapperSelect>
          )}
        </OverlayInputWrapper>
      </Form.Item>

      <Form.Item
        name='status'
        label='Status'
        {...statusValidateProps}
        {...fullWidthFormLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('status')}>
          <WrapperSelect
            getPopupContainer={() => document.getElementById('product-field')}
            disabled={
              !hasBypassProductInternalPermission &&
              productData?.status?.toLowerCase() === 'exception'
            }
            suffixIcon={
              isEdit ? (
                <ResetFormStatus
                  form={form}
                  productData={productData}
                  onClick={async (status) => {
                    await handleStatusChange(status);
                  }}
                />
              ) : null
            }
            allowClear={isEdit ? false : true}
            onSelect={handleStatusChange}
          >
            {[
              sortBy([...STATUS_PRODUCT], ['displayName']).map(
                (status, index) => {
                  return (
                    <Option
                      value={status?.value}
                      key={index}
                      disabled={
                       !hasBypassProductInternalPermission && (isEdit
                          ? disableStatusSelectionInEditMode({
                              valueOption: status.value,
                              ...productData,
                            })
                          : disableStatusSelectionInEditModeInCreateMode({
                              valueOption: status.value,
                              isPrivateLabel,
                              userInfo,
                            }))
                      }
                    >
                      {status?.displayName}
                    </Option>
                  );
                }
              ),
            ]}
          </WrapperSelect>
        </OverlayInputWrapper>
      </Form.Item>

      {isDisplayActiveProductBtn && !hasBypassProductInternalPermission && (
        <ChargeActiveProduct
          entityId={productData?.productId}
          productData={productData}
          entityType='Product'
          productForm={form}
          validateProducts={validateProducts}
          onRevalidateProduct={onRevalidateProduct}
          productImagery={productImagery}
          getFormPayload={getFormPayload}
        />
      )}
    </>
  );
};
